import groupBy from 'lodash/groupBy';
import type { useGetChangeRequests } from 'src/hooks/queries/change-request/useGetChangeRequests';

type GetChangeRequestsData = NonNullable<
  ReturnType<typeof useGetChangeRequests>['data']
>;

export type ChangeRequestResponses =
  GetChangeRequestsData['change_request'][0]['responses'];

/**
 * The subset of an approver response the level-ordering and active-level walk
 * read. A structural type so both the register's response rows and the approval
 * panel's (a superset) assign cleanly.
 */
type LevelWalkResponse = {
  Approved?: boolean | null;
  approver: {
    level?: {
      Id: string;
      ApprovalRuleType?: string | null;
      SequenceOrder?: number | null;
      approval?: { ParentId?: string | null } | null;
    } | null;
  };
};

const getUniqueLevelIds = (responses: ChangeRequestResponses) =>
  Array.from(new Set(responses.map((r) => r.approver.level?.Id)));

export const getMaxLevel = (responses: ChangeRequestResponses) => {
  if (!responses || responses.length === 0) {
    return 0;
  }

  return getUniqueLevelIds(responses).length;
};

export const getCurrentLevel = (responses: ChangeRequestResponses) => {
  if (!responses || responses.length === 0) {
    return 0;
  }

  // Group responses by level and track approval status
  // Only mark a level as approved if all approvers in that level have approved
  const levelStatus = new Map<string, { total: number; approved: number }>();

  for (const response of responses) {
    const levelId = response.approver.level?.Id;
    if (!levelId) {
      continue;
    }

    if (!levelStatus.has(levelId)) {
      levelStatus.set(levelId, { total: 0, approved: 0 });
    }

    const status = levelStatus.get(levelId)!;
    status.total++;
    if (response.Approved === true) {
      status.approved++;
    }
  }

  // Start at level 1 and increment for each level where all approvers have approved
  let currentLevel = 1;

  levelStatus.forEach(({ total, approved }) => {
    if (total === approved) {
      currentLevel++;
    }
  });

  return currentLevel;
};

/**
 * Sort key aligning the ACTIVE-level walk with the panel's DISPLAY order
 * (`getApprovalLevels`: per-entity approval's levels first, then the
 * GLOBAL approval's, SequenceOrder-asc within each). Without it the walk
 * follows `Object.values(groupBy(responses))` insertion order — i.e. the
 * raw responses array, which is seeding/query order — so with 1 object +
 * 2 global levels the first approvable level could be the global one the
 * panel displays as level 2 (RSP-6087 sequencing bug).
 *
 * The null branch is a defensive default — the synthetic NO-LEVEL bucket is
 * excluded from the walk before sorting.
 */
export const levelOrderKey = (
  level:
    | {
        SequenceOrder?: number | null;
        approval?: { ParentId?: string | null } | null;
      }
    | null
    | undefined
): [number, number] => {
  if (!level) {
    return [2, 0];
  }

  return [level.approval?.ParentId == null ? 1 : 0, level.SequenceOrder ?? 0];
};

/**
 * The CR's real level ids in display order (specific-first, SequenceOrder-asc).
 * The synthetic NO-LEVEL bucket (responses whose approver has no level) is
 * EXCLUDED: it has no ApprovalRuleType, so `getActiveLevelId`'s rule switch
 * would push no result for it and skew `levelResults` indices against this
 * array — and a level-less response can't be voted on anyway.
 */
const orderLevelIds = (levels: Record<string, LevelWalkResponse[]>) =>
  Object.keys(levels)
    .filter((levelId) => levelId !== 'NO-LEVEL')
    .sort((a, b) => {
      const [globalA, seqA] = levelOrderKey(levels[a][0]?.approver.level);
      const [globalB, seqB] = levelOrderKey(levels[b][0]?.approver.level);

      return globalA - globalB || seqA - seqB;
    });

const groupResponsesByLevelId = (responses: LevelWalkResponse[]) =>
  groupBy(responses, (response) => response.approver.level?.Id ?? 'NO-LEVEL');

/**
 * The level a CR is currently waiting on — the one the approval-steps panel
 * renders approve/reject controls for. `null` once every level is satisfied, or
 * once any level has been rejected (a rejected CR waits on nobody).
 *
 * Callers must not substitute "first response that isn't approved": that reads
 * the raw responses array, so it follows query order rather than level order,
 * and it ignores the level's approval rule (WOR-306).
 */
export const getActiveLevelId = (
  responses: LevelWalkResponse[]
): null | string => {
  const levels = groupResponsesByLevelId(responses);
  let rejected = false;

  const levelResults: boolean[] = [];

  const orderedLevelIds = orderLevelIds(levels);

  orderedLevelIds.forEach((levelId) => {
    // No empty-group guard needed: lodash groupBy only creates a key when at
    // least one response hashed to it, so every group here is non-empty.
    const level = levels[levelId];

    const responded = level.filter(
      (response) => response.Approved !== null
    ).length;
    const approvers = level.filter(
      (response) => response.Approved === true
    ).length;

    // Approval rule decisions
    switch (level[0].approver.level?.ApprovalRuleType) {
      case 'all_approve':
        // If the number of approvers is equal to the number of responses, then approve.
        levelResults.push(approvers === level.length);
        // If the number of responses is more than the number of approvers, then reject
        // since someone must have rejected it.
        if (responded > approvers) {
          rejected = true;
        }
        break;
      case 'any_one_approve':
        // If there is at least one approver, then approve
        levelResults.push(approvers > 0);
        // If everyone has responded and there are no approvers, then reject.
        if (responded === level.length && approvers === 0) {
          rejected = true;
        }
        break;
      case 'majority_approve':
        // If the number of approvers is more than half, then approve.
        levelResults.push(approvers > level.length / 2);
        // If at least half of the approvers have rejected, then reject since
        // it will be impossible to make a majority.
        if (responded - approvers >= level.length / 2) {
          rejected = true;
        }
        break;
      default:
        // Invariant: EVERY walked level pushes exactly one result —
        // `firstNonRejectedLevelIndex` indexes back into `orderedLevelIds`,
        // so a skipped push would skew the mapping. An unrecognised rule
        // type (impossible at write time — the schemas are enum-constrained
        // — but cheap to defend) counts as satisfied: the panel can't
        // render voting controls for a rule it can't evaluate.
        levelResults.push(true);
        break;
    }
  });

  const firstNonRejectedLevelIndex = levelResults.findIndex(
    (result) => !result
  );
  const activeLevelIndex =
    rejected || firstNonRejectedLevelIndex === -1
      ? null
      : firstNonRejectedLevelIndex;

  return activeLevelIndex !== null ? orderedLevelIds[activeLevelIndex] : null;
};

/**
 * The level reached after the active one, in the panel's display order. `null`
 * when the CR has no active level, or when the active level is the last.
 */
const getNextLevelId = (responses: LevelWalkResponse[]): null | string => {
  const activeLevelId = getActiveLevelId(responses);
  if (!activeLevelId) {
    return null;
  }

  const orderedLevelIds = orderLevelIds(groupResponsesByLevelId(responses));

  return orderedLevelIds[orderedLevelIds.indexOf(activeLevelId) + 1] ?? null;
};

const groupApproversByLevel = (responses: ChangeRequestResponses) => {
  const levelGroups = new Map<
    string | null,
    Array<{ id: string; label: string }>
  >();

  responses.forEach((response) => {
    const levelId = response.approver.level?.Id ?? null;

    const approver = {
      id: response.approver.OwnerApprover
        ? 'owner'
        : (response.approver.user?.Id ?? response.approver.group?.Id ?? ''),
      label: response.approver.OwnerApprover
        ? 'Owner'
        : (response.approver.user?.FriendlyName ??
          response.approver.group?.Name ??
          ''),
    };

    if (!levelGroups.has(levelId)) {
      levelGroups.set(levelId, []);
    }
    levelGroups.get(levelId)!.push(approver);
  });

  return levelGroups;
};

export const getCurrentApprovers = (responses: ChangeRequestResponses) => {
  if (responses.length === 0) {
    return [];
  }

  const activeLevelId = getActiveLevelId(responses);
  if (!activeLevelId) {
    return [];
  }

  const levelGroups = groupApproversByLevel(responses);

  return levelGroups.get(activeLevelId) ?? [];
};

export const getNextApprovers = (responses: ChangeRequestResponses) => {
  if (responses.length === 0) {
    return [];
  }

  const nextLevelId = getNextLevelId(responses);
  if (!nextLevelId) {
    return [];
  }

  const levelGroups = groupApproversByLevel(responses);

  return levelGroups.get(nextLevelId) ?? [];
};
